/**
 * Partner Access Management Page
 * Allows club administrators to manage partner API access tokens and game assignments
 */

import React, { useState, useEffect } from 'react';
import { partnerAccessApi, PartnerUserAdmin } from '../services/partnerAccessApi';
import { partnersApi } from '../services/partnersApi';
import { customPartnersApi } from '../services/customPartnersApi';
import {
  AccessToken,
  GamePartnerAssignment,
  TokenStatus,
  TokenStatusLabels,
  TokenStatusColors,
  CreatePartnerTokenRequest,
  CreatePartnerTokenResponse,
  CreatePartnerAccessRequest,
  CreatePartnerAccessResponse
} from '../types/partnerAccess';
import { Partner } from '../types/partners';
import CreateTokenModal from '../components/partnerAccess/CreateTokenModal';
import TokenCard from '../components/partnerAccess/TokenCard';
import AssignmentList from '../components/partnerAccess/AssignmentList';
import StatsOverview from '../components/partnerAccess/StatsOverview';
import { Mail, Users, CheckCircle, ChevronDown, ChevronUp, Ban, KeyRound, Trash2, ClipboardCopy, Plus, ShieldCheck } from 'lucide-react';
import { ToastContainer } from '../components/common/Toast';
import '../styles/PageLayout.css';

const PartnerAccess: React.FC = () => {
  // State
  const [tokens, setTokens] = useState<AccessToken[]>([]);
  const [assignments, setAssignments] = useState<GamePartnerAssignment[]>([]);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // UI State
  const [activeTab, setActiveTab] = useState<'tokens' | 'users' | 'stats'>('tokens');
  const [partnerUsers, setPartnerUsers] = useState<PartnerUserAdmin[]>([]);
  const [includeInactiveUsers, setIncludeInactiveUsers] = useState(false);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPartner, setFilterPartner] = useState<number | null>(null);
  
  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', partnerId: 0, role: 'partner_user', expiryDays: 7 });
  const [inviteResult, setInviteResult] = useState<{ inviteLink: string; email: string; partnerName: string } | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  
  // Toast
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type: 'success' | 'error' }>>([]);

  const showToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  // Load data
  const loadData = async () => {
    try {
      console.log('PartnerAccess: loadData() called, includeRevoked =', includeRevoked);
      setLoading(true);
      setError(null);

      console.log('PartnerAccess: Calling API endpoints...');
      const [tokensData, assignmentsData, globalPartnersData, customPartnersData] = await Promise.all([
        partnerAccessApi.getAllTokens(includeRevoked),
        partnerAccessApi.getAllAssignments(undefined, undefined, includeRevoked),
        partnersApi.getAll(false),
        customPartnersApi.getAll(false).catch(() => [])
      ]);

      // Merge global + custom partners (custom partners get negative IDs)
      const globalPartners: Partner[] = Array.isArray(globalPartnersData) ? globalPartnersData : [];
      const customAsPartners: Partner[] = Array.isArray(customPartnersData)
        ? customPartnersData.map(cp => ({
            id: cp.id > 0 ? -cp.id : cp.id, // Negative ID convention for custom partners
            name: `${cp.name} (Lokal)`,
            partnerType: cp.partnerType,
            description: cp.description,
            website: cp.website,
            contactEmail: cp.contactEmail,
            contactPhone: cp.contactPhone,
            address: cp.address,
            logo: cp.logo,
            isActive: cp.isActive,
            createdAt: cp.createdAt,
            isCustomPartner: true
          }))
        : [];
      const allPartners = [...globalPartners, ...customAsPartners];

      console.log('PartnerAccess: Received data - tokens:', tokensData?.length, 'assignments:', assignmentsData?.length, 'partners:', allPartners.length, '(global:', globalPartners.length, ', custom:', customAsPartners.length, ')');

      // Ensure data is arrays
      setTokens(Array.isArray(tokensData) ? tokensData : []);
      setAssignments(Array.isArray(assignmentsData) ? assignmentsData : []);
      setPartners(allPartners);

      // Load partner users
      try {
        const usersData = await partnerAccessApi.getPartnerUsers(includeInactiveUsers);
        setPartnerUsers(Array.isArray(usersData) ? usersData : []);
      } catch (usersErr) {
        console.warn('PartnerAccess: Could not load partner users (may not have permission):', usersErr);
        setPartnerUsers([]);
      }

      console.log('PartnerAccess: Data loaded successfully');
    } catch (err: any) {
      console.error('PartnerAccess: Error loading data:', err);
      setError('Fehler beim Laden der Daten');
      // Set empty arrays on error
      setTokens([]);
      setAssignments([]);
      setPartners([]);
      setPartnerUsers([]);
      showToast('Fehler beim Laden der Daten', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [includeRevoked, includeInactiveUsers]);

  // Handlers
  const handleCreateToken = async (request: CreatePartnerTokenRequest): Promise<CreatePartnerTokenResponse> => {
    try {
      console.log('PartnerAccess: handleCreateToken called with request:', request);
      const response = await partnerAccessApi.createPartnerToken(request);
      console.log('PartnerAccess: Received response from API:', response);
      showToast('Token erfolgreich erstellt', 'success');
      // DON'T reload data here - it will remount the modal and reset createdToken!
      // Data will be reloaded when modal is closed
      console.log('PartnerAccess: Returning response:', response);
      return response;
    } catch (err: any) {
      console.error('PartnerAccess: Error creating token:', err);
      showToast('Fehler beim Erstellen des Tokens: ' + err.message, 'error');
      throw err;
    }
  };

  const handleRevokeToken = async (tokenId: number) => {
    try {
      await partnerAccessApi.revokeToken(tokenId, { reason: 'Widerrufen durch Administrator' });
      showToast('Token erfolgreich widerrufen', 'success');
      await loadData();
    } catch (err: any) {
      console.error('Error revoking token:', err);
      showToast('Fehler beim Widerrufen des Tokens', 'error');
    }
  };

  const handleRevokeAssignment = async (assignmentId: number, reason: string) => {
    try {
      await partnerAccessApi.revokeAssignment(assignmentId, { reason });
      showToast('Zuweisung erfolgreich widerrufen', 'success');
      await loadData();
    } catch (err: any) {
      console.error('Error revoking assignment:', err);
      showToast('Fehler beim Widerrufen der Zuweisung', 'error');
    }
  };

  const handleCloseCreateModal = async () => {
    console.log('PartnerAccess: Closing create modal, reloading data...');
    setIsCreateModalOpen(false);
    // Reload data after modal is closed
    await loadData();
    console.log('PartnerAccess: Data reloaded after modal close');
  };

  const handleCreateInvite = async () => {
    if (!inviteForm.email || !inviteForm.partnerId) {
      showToast('Bitte Email und Partner ausfüllen', 'error');
      return;
    }
    try {
      setInviteLoading(true);
      const result = await partnerAccessApi.createInvite(inviteForm);
      setInviteResult({ inviteLink: result.inviteLink, email: result.email, partnerName: result.partnerName || '' });
      showToast('Einladung erfolgreich erstellt', 'success');
    } catch (err: any) {
      showToast('Fehler: ' + err.message, 'error');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCloseInviteModal = () => {
    setIsInviteModalOpen(false);
    setInviteForm({ email: '', partnerId: 0, role: 'partner_user', expiryDays: 7 });
    setInviteResult(null);
  };

  const handleToggleUserRole = async (user: PartnerUserAdmin) => {
    const newRole = user.role === 'partner_admin' ? 'partner_user' : 'partner_admin';
    try {
      await partnerAccessApi.updatePartnerUser(user.id, { role: newRole });
      showToast(`Rolle von ${user.email} auf ${newRole === 'partner_admin' ? 'Admin' : 'Benutzer'} geändert`, 'success');
      await loadData();
    } catch (err: any) {
      showToast('Fehler: ' + err.message, 'error');
    }
  };

  const handleToggleUserActive = async (user: PartnerUserAdmin) => {
    try {
      await partnerAccessApi.updatePartnerUser(user.id, { isActive: !user.isActive });
      showToast(user.isActive ? `${user.email} deaktiviert` : `${user.email} aktiviert`, 'success');
      await loadData();
    } catch (err: any) {
      showToast('Fehler: ' + err.message, 'error');
    }
  };

  const handleResetPassword = async (user: PartnerUserAdmin) => {
    const newPassword = window.prompt(`Neues Passwort für "${user.email}" eingeben (min. 8 Zeichen):`);
    if (!newPassword) return;
    if (newPassword.length < 8) {
      showToast('Passwort muss mindestens 8 Zeichen lang sein', 'error');
      return;
    }
    try {
      await partnerAccessApi.resetPartnerUserPassword(user.id, newPassword);
      showToast(`Passwort für ${user.email} wurde zurückgesetzt`, 'success');
    } catch (err: any) {
      showToast('Fehler: ' + err.message, 'error');
    }
  };

  const handleDeleteUser = async (user: PartnerUserAdmin) => {
    if (!window.confirm(`Benutzer "${user.email}" wirklich endgültig löschen?\n\nDiese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }
    try {
      await partnerAccessApi.deletePartnerUser(user.id);
      showToast(`${user.email} wurde gelöscht`, 'success');
      await loadData();
    } catch (err: any) {
      showToast('Fehler: ' + err.message, 'error');
    }
  };

  // Filter data
  const filteredTokens = Array.isArray(tokens) ? tokens.filter(token => {
    const matchesSearch = token.tokenName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         token.partnerName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPartner = !filterPartner || token.partnerId === filterPartner;
    return matchesSearch && matchesPartner;
  }) : [];

  const filteredAssignments = Array.isArray(assignments) ? assignments.filter(assignment => {
    const matchesSearch = assignment.partnerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         assignment.gameDescription?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPartner = !filterPartner || assignment.partnerId === filterPartner;
    return matchesSearch && matchesPartner;
  }) : [];

  if (loading) {
    return (
      <div className="page-container"><div className="page-loading"><div className="page-spinner" /><p>Laden...</p></div></div>
    );
  }

  return (
    <div className="page-container">
      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={() => setIsInviteModalOpen(true)}>
            <Mail size={14} /> Partner einladen
          </button>
          <button className="page-btn page-btn-create" onClick={() => setIsCreateModalOpen(true)}>
            <Plus size={14} /> Neuer Token
          </button>
          <div className="page-controls-spacer" />
          <span className="page-record-count">{tokens.length} Tokens · {partnerUsers.length} Benutzer</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="page-tabs">
        <button className={`page-tab${activeTab === 'tokens' ? ' active' : ''}`} onClick={() => setActiveTab('tokens')}>
          Tokens ({tokens.length})
        </button>
        <button className={`page-tab${activeTab === 'users' ? ' active' : ''}`} onClick={() => setActiveTab('users')}>
          <Users size={14} /> Benutzer ({partnerUsers.length})
        </button>
        <button className={`page-tab${activeTab === 'stats' ? ' active' : ''}`} onClick={() => setActiveTab('stats')}>
          Statistiken
        </button>
      </div>

      {/* Filters */}
      <div className="page-controls">
        <div className="page-controls-left">
          <input className="page-search-input" type="text" placeholder="Suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          <select className="page-filter-select" value={filterPartner || ''} onChange={(e) => setFilterPartner(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Alle Partner</option>
            {Array.isArray(partners) && partners.map(partner => (
              <option key={partner.id} value={partner.id}>{partner.name}</option>
            ))}
          </select>
          <label className="page-checkbox-label">
            <input type="checkbox" checked={includeRevoked} onChange={(e) => setIncludeRevoked(e.target.checked)} />
            Widerrufene anzeigen
          </label>
        </div>
      </div>

      {/* Content */}
      <div>
        {activeTab === 'tokens' && (
          <div className="page-card-grid">
            {filteredTokens.length === 0 ? (
              <div className="page-empty-state">
                <p>Keine Tokens gefunden</p>
                <button className="page-toggle-btn page-btn-create" onClick={() => setIsCreateModalOpen(true)}>
                  Ersten Token erstellen
                </button>
              </div>
            ) : (
              filteredTokens.map(token => (
                <TokenCard
                  key={token.tokenId}
                  token={token}
                  onRevoke={handleRevokeToken}
                  onRefresh={loadData}
                />
              ))
            )}
          </div>
        )}



        {activeTab === 'users' && (
          <div>
            <div className="page-controls" style={{ marginBottom: 12 }}>
              <label className="page-checkbox-label">
                <input type="checkbox" checked={includeInactiveUsers} onChange={(e) => setIncludeInactiveUsers(e.target.checked)} />
                Inaktive anzeigen
              </label>
            </div>
            {partnerUsers.length === 0 ? (
              <div className="page-empty-state">
                <p>Keine Partner-Benutzer vorhanden</p>
                <p className="page-table-meta">Lade Partner über den "Partner einladen" Button ein.</p>
              </div>
            ) : (
              <table className="page-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Partner</th>
                    <th>Rolle</th>
                    <th>Status</th>
                    <th>Letzter Login</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {partnerUsers.map(user => (
                    <tr key={user.id} style={{ opacity: user.isActive ? 1 : 0.5 }}>
                      <td>
                        {user.email}
                        {user.emailVerified && <span title="Email verifiziert"><CheckCircle size={12} /></span>}
                      </td>
                      <td>{user.displayName || '–'}</td>
                      <td>{user.partnerName || `#${user.partnerId}`}</td>
                      <td>
                        <span className="page-badge" style={{
                          backgroundColor: user.role === 'partner_admin' ? '#ffc107' : '#17a2b8',
                          color: user.role === 'partner_admin' ? '#000' : '#fff',
                        }}>
                          {user.role === 'partner_admin' ? 'Admin' : 'Benutzer'}
                        </span>
                      </td>
                      <td>
                        <span className="page-badge" style={{
                          backgroundColor: user.isActive ? '#28a745' : '#dc3545',
                          color: '#fff',
                        }}>
                          {user.isActive ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td className="page-table-meta">
                        {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString('de-DE') : 'Noch nie'}
                      </td>
                      <td>
                        <div className="page-action-group">
                          <button className="page-toggle-btn" onClick={() => handleToggleUserRole(user)} title={user.role === 'partner_admin' ? 'Zu Benutzer herabstufen' : 'Zum Admin befördern'}>
                            {user.role === 'partner_admin' ? <><ChevronDown size={12} /> Benutzer</> : <><ChevronUp size={12} /> Admin</>}
                          </button>
                          <button className="page-toggle-btn" onClick={() => handleToggleUserActive(user)} title={user.isActive ? 'Deaktivieren' : 'Aktivieren'} style={{ backgroundColor: user.isActive ? '#fff3cd' : '#d4edda' }}>
                            {user.isActive ? <><Ban size={12} /> Deaktivieren</> : <><CheckCircle size={12} /> Aktivieren</>}
                          </button>
                          <button className="page-toggle-btn" onClick={() => handleResetPassword(user)} title="Passwort zurücksetzen" style={{ backgroundColor: '#cfe2ff', color: '#084298' }}>
                            <KeyRound size={12} /> Passwort
                          </button>
                          <button className="page-toggle-btn page-btn-delete" onClick={() => handleDeleteUser(user)} title="Benutzer endgültig löschen">
                            <Trash2 size={12} /> Löschen
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {activeTab === 'stats' && (
          <StatsOverview />
        )}
      </div>

      {/* Modals */}
      {isCreateModalOpen && (
        <CreateTokenModal
          partners={partners}
          onClose={handleCloseCreateModal}
          onCreate={handleCreateToken}
        />
      )}

      {/* Invite Modal */}
      {isInviteModalOpen && (
        <div className="page-modal-overlay" onClick={handleCloseInviteModal}>
          <div className="page-modal-content" onClick={e => e.stopPropagation()}>
            <h3><Mail size={16} /> Partner einladen</h3>
            {inviteResult ? (
              <div>
                <p style={{ color: '#28a745', fontWeight: 'bold', marginBottom: '1rem' }}>
                  <CheckCircle size={14} /> Einladung erstellt!
                </p>
                <div className="page-detail-box">
                  <p><strong>Email:</strong> {inviteResult.email}</p>
                  <p><strong>Partner:</strong> {inviteResult.partnerName}</p>
                  <p><strong>Einladungslink:</strong></p>
                  <input className="page-search-input" type="text" value={inviteResult.inviteLink} readOnly onClick={e => (e.target as HTMLInputElement).select()} style={{ width: '100%', fontSize: '0.85rem' }} />
                  <p className="page-table-meta" style={{ marginTop: '0.5rem' }}>
                    Diesen Link an den Partner senden. Er kann sich damit registrieren.
                  </p>
                  <div className="page-modal-actions" style={{ marginTop: '1rem' }}>
                    <button className="page-toggle-btn page-btn-create" style={{ flex: 1, backgroundColor: '#17a2b8', borderColor: '#17a2b8' }} onClick={() => { navigator.clipboard.writeText(inviteResult.inviteLink); showToast('Link in Zwischenablage kopiert!', 'success'); }}>
                      <ClipboardCopy size={14} /> Link kopieren
                    </button>
                    <button className="page-toggle-btn page-btn-create" style={{ flex: 1, backgroundColor: '#0078d4', borderColor: '#0078d4' }} onClick={() => {
                      const subject = encodeURIComponent(`Einladung zum VoyagePlanner Partner-Portal`);
                      const body = encodeURIComponent(`Hallo,\n\nSie wurden als Partner "${inviteResult.partnerName}" zum VoyagePlanner Partner-Portal eingeladen.\n\nBitte klicken Sie auf den folgenden Link, um sich zu registrieren:\n${inviteResult.inviteLink}\n\nBitte beachten Sie, dass dieser Link nur einmalig verwendet werden kann und zeitlich begrenzt gültig ist.\n\nMit freundlichen Grüßen\nVoyagePlanner Team`);
                      window.open(`mailto:${inviteResult.email}?subject=${subject}&body=${body}`, '_self');
                    }}>
                      <Mail size={14} /> Einladungsmail senden
                    </button>
                  </div>
                </div>
                <button className="page-toggle-btn page-btn-create" style={{ width: '100%' }} onClick={handleCloseInviteModal}>Schließen</button>
              </div>
            ) : (
              <div>
                <div className="page-form-field">
                  <label>Email des Partners *</label>
                  <input type="email" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} placeholder="partner@example.com" />
                </div>
                <div className="page-form-field">
                  <label>Partner *</label>
                  <select value={inviteForm.partnerId} onChange={e => setInviteForm(f => ({ ...f, partnerId: Number(e.target.value) }))}>
                    <option value={0}>Partner wählen...</option>
                    {partners.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
                  </select>
                </div>
                <div className="page-form-field">
                  <label>Rolle</label>
                  <select value={inviteForm.role} onChange={e => setInviteForm(f => ({ ...f, role: e.target.value }))}>
                    <option value="partner_user">Partner User</option>
                    <option value="partner_admin">Partner Admin</option>
                  </select>
                </div>
                <div className="page-form-field">
                  <label>Gültigkeitsdauer (Tage)</label>
                  <input type="number" value={inviteForm.expiryDays} onChange={e => setInviteForm(f => ({ ...f, expiryDays: Number(e.target.value) }))} min={1} max={90} style={{ width: '120px' }} />
                </div>
                <div className="page-modal-actions">
                  <button className="page-toggle-btn" onClick={handleCloseInviteModal}>Abbrechen</button>
                  <button className="page-toggle-btn page-btn-create" onClick={handleCreateInvite} disabled={inviteLoading}>
                    {inviteLoading ? 'Wird erstellt...' : 'Einladung senden'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Toast */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};



export default PartnerAccess;

